<template>
  <div
    class="landing-page-container page landing-page"
    :class="'landing-page-' + ldpHandle"
  >
    <template v-if="landingPageStore.landingPage && !loading">
      <template
        v-for="(section, index) in landingPageStore.landingPage.layout"
        :key="index"
      >
        <client-only
          :is-show="shouldShowComponents[section._key] || false"
        >
          <template #placeholder>
            <div ref="placeholders" :data-key="section._key"></div>
            <sib-skeleton-loading
              height="5em"
              class="mb-sm"
            ></sib-skeleton-loading>
          </template>
          <component
            v-if="section.lazy"
            :is="landingPageLayout[section._type]"
            :section="section"
          />
        </client-only>
        <component
          v-if="!section.lazy"
          :is="landingPageLayout[section._type]"
          :section="section"
        />
      </template>
    </template>
    <div v-else-if="loading" class="landing-page-loading">
      <sib-skeleton-loading height="30em"></sib-skeleton-loading>
      <h2>
        <sib-skeleton-loading height="2em" width="60%"></sib-skeleton-loading>
      </h2>
      <sib-skeleton-loading height="15em"></sib-skeleton-loading>
      <p>
        <sib-skeleton-loading></sib-skeleton-loading>
        <sib-skeleton-loading width="30%"></sib-skeleton-loading>
      </p>
      <p>
        <sib-skeleton-loading width="80%"></sib-skeleton-loading>
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import useLandingPageStore from '@/store/landingPage'
import {
  computed,
  defineAsyncComponent,
  onMounted,
  onServerPrefetch,
  onUnmounted,
  reactive,
  ref,
} from 'vue'
import { useRoute } from 'vue-router'
import CacheService from '@/services/cache'
import Logger from '@/services/log'
import { useFetchData } from '@/composables/fetchData'
import { usePage } from '@/composables/page'
import { QUERY_STRING_NO_CACHE } from '@/utils/constants'
import { LandingPage, NullOrType } from '@/types'
import { cloneDeep, EVENT_LANDING_PAGE_VIEWED } from '@/utils'
import Analytics from '@/services/analytics'

const route = useRoute()
const landingPageStore = useLandingPageStore()
const { setPageTitle } = usePage()

const ldpHandle = computed(() => {
  return route.params.ldpHandle as string
})

const shouldShowComponents = reactive<{ [key: string]: boolean }>({})
const placeholders = ref([])

const landingPageLayout: { [key: string]: any } = {
  heading: defineAsyncComponent(() => import('./Heading.vue')),
  banner: defineAsyncComponent(() => import('./Banner.vue')),
  heroImage: defineAsyncComponent(() => import('./HeroImage.vue')),
  collection: defineAsyncComponent(() => import('./Collection.vue')),
  collection2: defineAsyncComponent(() => import('./Collection2.vue')),
  customHtml: defineAsyncComponent(() => import('./CustomHtml.vue')),
  donationTable: defineAsyncComponent(() => import('./DonationTable.vue')),
  manualCollection: defineAsyncComponent(
    () => import('./ManualCollection.vue')
  ),
  collectionGroup: defineAsyncComponent(() => import('./CollectionGroup.vue')),
  bannerCollections: defineAsyncComponent(
    () => import('./BannerCollections.vue')
  ),
  productCategories: defineAsyncComponent(
    () => import('./ProductCategories.vue')
  ),
  products: defineAsyncComponent(() => import('./Products.vue')),
  reivews: defineAsyncComponent(() => import('./Reviews.vue')),
  bannerAndBackground: defineAsyncComponent(
    () => import('./BannerAndBackground.vue')
  ),
  product2: defineAsyncComponent(() => import('./Product2.vue')),
  imageGroup: defineAsyncComponent(() => import('./Imagegroup.vue')),
  collectionsCarousel: defineAsyncComponent(() => import('./CollectionsCarousel.vue')),
  productsCarousel: defineAsyncComponent(() => import('./ProductsCarousel.vue')),
  siteFeatures: defineAsyncComponent(() => import('./SiteFeatures.vue')),
  contentList: defineAsyncComponent(() => import('./ContentList.vue')),
}

const { fetchData, loading, resetVariables } = useFetchData({
  fetchDataFunction: fetchLaningPage,
  checkNotFoundFunction: checkNotFound,
  setNotFoundFunction: setLandingPageNotFound,
})

onServerPrefetch(async () => {
  await fetchData()

  // reset variables to prevent hydration mismatch in case can not fetch data.
  // we will try to load again on the client
  resetVariables()

  if (landingPageStore.landingPage) {
    setPageTitle(landingPageStore.landingPage.name)
  }
})

function trackingLandingPageViewed(landingPage: any) {
  Analytics.track(EVENT_LANDING_PAGE_VIEWED, {
    handle: landingPage.handle,
    name: landingPage.name,
  })
}

onMounted(async () => {
  if (landingPageStore.isLandingPageNotFound) return
  if (!landingPageStore.landingPage) {
    await fetchData()
  } else {
    if (landingPageStore.landingPage.__cacheKey) {
      Logger.log(
        'Landing Page',
        'Set landing page from server side to cache',
        3
      )
      // save page from server to local cache
      CacheService.instance?.set(
        landingPageStore.landingPage.__cacheKey,
        cloneDeep(landingPageStore.landingPage)
      )
    }
  }

  if (landingPageStore.landingPage?.layout) {
    landingPageStore.landingPage.layout.forEach((section) => {
      shouldShowComponents[section._key] = false
    })

    const intersectCallback = function (entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const componentKey = (entry.target as HTMLElement).dataset.key

          // show section landing page layout
          if (componentKey) shouldShowComponents[componentKey] = true

          const placeholder = placeholders.value.find(
            (placeholder) => placeholder.dataset.key === componentKey
          )

          // unobserve placeholder
          if (placeholder && observer) {
            observer.unobserve(placeholder)
          }
        }
      })
    }

    const options = {
      rootMargin: `${document.body.clientHeight / 2}px`,
    }

    const observer = new IntersectionObserver(intersectCallback, options)

    placeholders.value.forEach((placeholder) => {
      observer.observe(placeholder)
    })
  }
  trackingLandingPageViewed(landingPageStore.landingPage)
})

onUnmounted(() => {
  landingPageStore.landingPage = undefined
})

async function fetchLaningPage() {
  if (ldpHandle.value) {
    return await landingPageStore.fetchLandingPage({
      handle: ldpHandle.value.toString(),
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    })
  }
  return null
}

function checkNotFound(landingPage: NullOrType<LandingPage>) {
  return !landingPage
}

function setLandingPageNotFound(isNotFound: boolean) {
  landingPageStore.isLandingPageNotFound = isNotFound
}
</script>

<style lang="scss">
.landing-page-container {
  & > * {
    padding-left: var(--container-padding);
    padding-right: var(--container-padding);
  }
}
</style>
